/** Imports */
import {z} from 'zod';
import validator from 'validator';
import {type Widget} from '@/components/Page_Reportings/Widget';
import {type Metric} from '@/mongoSchemas/Organization/Metrics.zod';
import {type Dashboard} from '@/mongoSchemas/Organization/Dashboards.zod';


/** Types */
export type MongoObjectId = z.infer<typeof zMongoObjectId>;
export const ACTION_SUCCESS = true as const;
export const ACTION_FAILURE = false as const;
export type ActionResponseError = {
    success: false;
    error: string;
    errorCode?: string;
    data: null
};
export type ActionResponseSuccess<T> = {
    success: true;
    data: T extends void ? undefined : Jsonified<T>;
};
export type ActionResponse<T = void> = Promise<ActionResponseError | ActionResponseSuccess<T>>;
export type tFunction = (phrase: string, ...args: Array<any>) => string;
export type PostMessageEventData = z.infer<typeof zPostMessageEventData>;
export type QueryParamValue = undefined | string | Array<string>;
export type Override<T, U> = Omit<T, keyof U> & U;
type AnyFn = (...args: Array<unknown>) => unknown;
type Prev = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
export type Jsonified<T, Depth extends number = 10> =
    Depth extends 0
        ? unknown
        : T extends {toJSON(): infer U}
            ? Jsonified<U, Prev[Depth]>
            : T extends Date
                ? string
                : T extends string | number | boolean | null
                    ? T
                    : T extends undefined | symbol | bigint | AnyFn
                        ? never
                        : T extends ReadonlyArray<infer U>
                            ? Array<Jsonified<U, Depth>>
                            : T extends object
                                ? {
                                    [K in keyof T as Jsonified<T[K], Prev[Depth]> extends never
                                        ? never
                                        : K]
                                    : Jsonified<T[K], Prev[Depth]>
                                } : never;
export type SentimentConfig = {
    sentiment: 'POSITIVE' | 'NEGATIVE' | 'NEUTRAL';
    color: string;
    emoji: string;
};


/** Constants */
const isMongoId = validator.isMongoId;


/** Zod schemas */
export const zMongoObjectId = z
    .unknown()
    .transform((val) => String(val))
    .refine((val) => isMongoId(val), {
        message: 'Invalid Mongo ObjectId',
    });
export const zMongoObject = z.object({
    _id: zMongoObjectId,
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export const zPostMessageEventData = z.object({
    event: z.string(),
    success: z.boolean(),
    data: z.any().optional(),
    error: z.string().optional(),
});


/** Custom events */
declare global {
    // noinspection JSUnusedGlobalSymbols
    interface WindowEventMap {
        'Reportings:SetAddEditDashboardDialogState': CustomEvent<{
            open: boolean;
            dashboard?: Jsonified<Dashboard> | null;
            isNew?: boolean;
        }>;
        'Reportings:SetModifyWidgetsDialogState': CustomEvent<{
            open: boolean;
            widgets: Array<string>;
        }>;
        'Reportings:SetAutomaticExportDialogState': CustomEvent<{
            open: boolean;
        }>;
        'Reportings:SetExportDashboardDialogState': CustomEvent<{
            open: boolean;
            metrics: Array<Jsonified<Metric>>;
        }>;
        'Reportings:SetOptionsDialogState': CustomEvent<{
            open: boolean;
            metric: Jsonified<Metric>;
            widget: Widget;
        }>;
        'Reportings:SetInfoDialogState': CustomEvent<{
            open: boolean;
            widget: Widget;
            metricId?: string;
        }>;
    }
}

/**
 * Type guard that filters out nullish and empty-string values.
 *
 * @typeParam T - The value type to preserve after narrowing.
 * @param value - The value to check.
 * @returns `true` if the value is not `null`, `undefined`, or an empty string.
 *
 * @example
 * This is useful with array filtering to remove empty entries while keeping the remaining values strongly typed:
 * `values.filter(nullishFilter)`
 */
export function nullishFilter<T>(value: T | null | undefined | ''): value is T {
    return (
        (value !== null)
        && (value !== undefined)
        && (
            (typeof value !== 'string')
            || (value.trim().length > 0)
        )
    );
}
